/**
 * Closing the deliveries that were handed over and never marked done.
 *
 * "Dispatched" does not take an order off the board and nothing moves it on
 * afterwards, so every delivery the shop makes stays open and, once its hour
 * passes, joins Overdue for good. A month of that buries the few orders
 * genuinely running late under a hundred that were delivered weeks ago, which
 * makes the one tab that should be urgent worth ignoring.
 *
 * It shows what it found before it touches anything — a count, a date range
 * and a money total — because this moves real orders, and because the figure
 * it reports is the thing worth sanity-checking before agreeing.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PackageCheck } from 'lucide-react';
import { formatMoney } from '@bakery/shared';

import { api } from '../lib/api.js';
import { Button, Modal, Skeleton, useToast } from '../components/ui.js';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-PH', { day: 'numeric', month: 'long', year: 'numeric' });

export function StaleDeliveriesBanner() {
  const [open, setOpen] = useState(false);
  const stale = useQuery({ queryKey: ['orders', 'stale-deliveries'], queryFn: api.orders.staleDeliveries });

  if (!stale.data || stale.data.count === 0) return null;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-warn-soft)] px-3 py-2.5">
        <p className="text-sm">
          <strong className="tabular">{stale.data.count}</strong> delivered order
          {stale.data.count === 1 ? '' : 's'} never marked complete, so they are sitting in Overdue.
        </p>
        <Button size="sm" variant="secondary" icon={<PackageCheck className="size-4" />} onClick={() => setOpen(true)}>
          Review and close
        </Button>
      </div>
      {open && <StaleDeliveriesModal onClose={() => setOpen(false)} />}
    </>
  );
}

function StaleDeliveriesModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const stale = useQuery({ queryKey: ['orders', 'stale-deliveries'], queryFn: api.orders.staleDeliveries });

  const close = useMutation({
    mutationFn: () => api.orders.closeStaleDeliveries(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.push({
        tone: 'ok',
        title: `${result.closed} order${result.closed === 1 ? '' : 's'} closed`,
        body: 'Each one is dated to the day it went out, so your sales figures are unchanged.',
      });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not close them', body: error.message }),
  });

  const data = stale.data;

  return (
    <Modal
      open
      onClose={onClose}
      title="Close delivered orders"
      description="These were dispatched and never marked complete, which is why they show as overdue."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={close.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={close.isPending}
            disabled={!data || data.count === 0}
            onClick={() => close.mutate()}
          >
            Close {data?.count ?? 0} order{data?.count === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      {stale.isLoading || !data ? (
        <Skeleton className="h-40" />
      ) : (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-[var(--color-ink-faint)]">Orders</dt>
              <dd className="tabular text-lg font-semibold">{data.count}</dd>
            </div>
            <div>
              <dt className="text-[var(--color-ink-faint)]">Worth</dt>
              <dd className="tabular text-lg font-semibold">{formatMoney(data.total)}</dd>
            </div>
            {data.oldest && (
              <div className="col-span-2">
                <dt className="text-[var(--color-ink-faint)]">Delivered between</dt>
                <dd>
                  {day(data.oldest)} and {data.newest ? day(data.newest) : day(data.oldest)}
                </dd>
              </div>
            )}
          </dl>

          {/*
            * Said plainly, because it is the question anybody sensible asks
            * before agreeing to close a month of orders in one press.
            */}
          <p className="rounded-[var(--radius-md)] bg-[var(--color-surface-sunken)] px-3 py-2.5 text-sm text-[var(--color-ink-soft)]">
            Each one is recorded as completed on the day it was due — the day the bread actually went out — so your
            dashboard and Daily report stay exactly as they are. Nothing is sent to any customer.
          </p>

          <div className="max-h-56 overflow-auto rounded-[var(--radius-md)] border border-[var(--color-line)]">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[var(--color-line)]">
                {data.orders.map((order) => (
                  <tr key={order.orderId}>
                    <td className="px-3 py-1.5 font-medium">{order.orderNo}</td>
                    <td className="px-3 py-1.5 text-[var(--color-ink-soft)]">{order.customerName ?? 'Walk-in'}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-[var(--color-ink-soft)]">{day(order.dueAt)}</td>
                    <td className="tabular px-3 py-1.5 text-right">{formatMoney(order.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
